import { h } from 'preact';
import { PLACEHOLDER_TYPE } from 'utils/constants';
import { getMovieTypeLabel } from 'utils/data';
import { missingReleaseDate } from 'utils/errors';
import * as jsonPointer from 'utils/jsonPointer';
import slugify from 'utils/slugify';
import { getHubThundrData } from 'utils/thundr';
import { joinClasses } from 'utils/utils';
import { formatReleaseMessaging, getReleaseDate, getReleaseDay, isReleaseWeek } from 'src_overrides/studios/universal-pictures-cms/utils/releaseMessaging';

import Badge from 'components/badge';
import MovieInfoEditor from 'components/editor/movies/movieInfo/movieInfo';
import SimpleField from 'components/editor/settings/simpleField';
import Info from 'components/icons/info.js';

import s from 'components/editor/movies/movieInfo/movieInfoUniversal.sss';

const MOVIE_INFO_TAB = 'movieInfo';
const RELEASE_MESSAGING_TAB = 'releaseMessaging';

const COMING_SOON_COPY_KEY = '$RELEASE_FUTURE';
const WEEK_OF_RELEASE_COPY_KEY = '$RELEASE_WEEK';
const THEATERS_COPY_KEY = '$RELEASE_THEATERS';
const THEATERS_HOMEENT_COPY_KEY = '$RELEASE_THEATERS_HOMEENT';
const HOMEENT_COPY_KEY = '$RELEASE_HOMEENT';

export default class UniversalMovieInfoEditor extends MovieInfoEditor {

	get requiresThundrData() {
		return true;
	}

	get showReleaseMessagingTab() {
		if (this.movie.placeholder) return false;
		if (this.isLocalMoviePage) return true;
		// Unlinked movies should be considered as having the feature available
		return !this.movie?.thundr_id || this.hasFeature('release-messaging:2');
	}

	constructor(props) {
		super(props);
		this.state.activeTab = MOVIE_INFO_TAB;
	}

	componentDidMount() {
		super.componentDidMount();
		this.loadHubsiteThundrData();

		this.hasReleaseMessagingOverride = !!this.movie?.releaseMessagingOverride;
	}

	componentDidUpdate(prevProps) {
		super.componentDidUpdate?.(prevProps);

		if (prevProps.data?.id !== this.props.data?.id) {
			this.hasReleaseMessagingOverride = !!this.movie?.releaseMessagingOverride;
		}

		// Feature availability is only known once thundrData has loaded for the current movie
		if (this.state.activeTab === RELEASE_MESSAGING_TAB && !this.showReleaseMessagingTab) {
			this.setState({ activeTab: MOVIE_INFO_TAB });
		}
	}

	async loadHubsiteThundrData() {
		const hubsiteThundrData = await getHubThundrData();
		this.setState({ hubsiteThundrData });
	}

	getMovieCopy(key) {
		return this.state.thundrData?.copy?.baseCopy?.[key] || this.state.hubsiteThundrData?.copy?.baseCopy?.[key];
	}

	slugMatchesTitle() {
		if (super.slugMatchesTitle()) return true;

		// If the current slug matches the base movie title, we also consider it a match
		const baseAsSlug = slugify(jsonPointer.get(this.props.data, `/base_movie/content/title`), { removeDiacritics: true });
		const currentSlug = jsonPointer.get(this.props.data, `/content/slug`);
		return currentSlug === baseAsSlug;
	}

	getSharedFieldLabel() {
		const movieType = this.movie?.movie_type;
		const hasPlaceholderOption = !this.props.data?.commits?.publish?.published_status?.endsWith(movieType.toUpperCase());
		if (!hasPlaceholderOption) return null; // Only show message when toggle type available
		const movieTypeLabel = getMovieTypeLabel(this.movie?.placeholder ? movieType : PLACEHOLDER_TYPE);
		return this.movie?.placeholder ? `Shared with ${movieTypeLabel}` : 'Shared with Holding Page';
	}

	renderTitle() {
		return super.renderTitle({
			note: this.getSharedFieldLabel(),
			inheritPath: `${this.basePath}/base_movie/content/title`,
			alsoSet: [{
				path: `${this.basePath}/content/slug`,
				method: (text) => {
					// If the title is empty, use the base movie title to generate the slug
					text ||= jsonPointer.get(this.props.data, `/base_movie/content/title`) || '';
					return slugify(text, { removeDiacritics: true });
				},
				condition: this.slugMatchesTitle
			}],
		});
	}

	renderReleaseDate() {
		return super.renderReleaseDate({
			note: this.getSharedFieldLabel(),
		});
	}

	renderSlug() {
		return super.renderSlug({
			note: this.getSharedFieldLabel(),
		});
	}

	renderExternalUrl() {
		return super.renderExternalUrl({
			help: 'Sets a redirect to the specified URL. Used for movies that are hosted on a vanity URL.'
		});
	}

	renderShowtimesSource() {
		return super.renderShowtimesSource({
			inheritPath: `${this.basePath}/base_movie/content/showtimesId`,
		});
	}

	renderMagicGenre(opts = {}) {
		const hasBaseMovie = jsonPointer.has(this.props.data, `/base_movie`);
		const baseData = jsonPointer.get(this.props.data, `/base_movie/content/dataLayer/magicGenre`);

		return super.renderMagicGenre({
			inheritPath: `${this.basePath}/base_movie/content/dataLayer/magicGenre`,
			tooltipInherit: `Inherited from Global Settings.`,
			defaultValue: baseData,
			disabled: hasBaseMovie,
			...opts,
		});
	}

	renderEssenceGenre(opts = {}) {
		const hasBaseMovie = jsonPointer.has(this.props.data, `/base_movie`);
		const baseData = jsonPointer.get(this.props.data, `/base_movie/content/dataLayer/essenceGenre`);

		return super.renderEssenceGenre({
			inheritPath: `${this.basePath}/base_movie/content/dataLayer/essenceGenre`,
			tooltipInherit: `Inherited from Global Settings.`,
			defaultValue: baseData,
			disabled: hasBaseMovie,
			...opts,
		});
	}

	renderIMDBId(opts = {}) {
		const hasBaseMovie = jsonPointer.has(this.props.data, `/base_movie`);
		const baseData = jsonPointer.get(this.props.data, `/base_movie/content/dataLayer/imdbId`);

		return super.renderIMDBId({
			inheritPath: `${this.basePath}/base_movie/content/dataLayer/imdbId`,
			tooltipInherit: `Inherited from Global Settings.`,
			defaultValue: baseData,
			disabled: hasBaseMovie,
			...opts,
		});
	}

	renderFieldsPlaceholder() {
		return [
			this.renderTitle(),
			this.renderReleaseDate(),
			this.renderSlug(),
		];
	}

	renderPreview(path, defaultText = '') {
		const error = missingReleaseDate(this.props.data) && 'Error: missing valid release date';

		const previewCopy = jsonPointer.get(this.props.data, path) || defaultText;
		const previewText = formatReleaseMessaging(previewCopy, this.movie, this.state.thundrData?.copy?.baseCopy);
		return (
			<div class={s.preview}>
				<div>Preview</div>
				<div class={joinClasses(s.previewText, error && s.error)}>{error || previewText}</div>
			</div>
		);
	}

	renderReleaseDateVariableOptions() {
		const movie = this.movie;
		const release = movie?.dates?.release;

		return (
			<datalist type="keywords">
				<option value="DATE" example={getReleaseDate(movie, this.state.thundrData?.copy?.baseCopy)} disabled={!release}>Release Date</option>
				<option value="DAY" example={getReleaseDay(movie, this.state.thundrData?.copy?.baseCopy)} disabled={release?.split('-')?.length !== 3}>Release Day</option>
			</datalist>
		);
	}

	renderComingSoon() {
		const placeholder = this.getMovieCopy(COMING_SOON_COPY_KEY) || 'In Theaters %DATE%';
		const live = (this.movie?.status?.theaters === 'comingSoon' || this.movie?.status?.theaters === 'preSale') && this.movie?.status?.homeEnt !== 'homeEnt' && !isReleaseWeek(this.movie?.dates?.release);

		return (
			<div class={s.fieldWithPreview}>
				<SimpleField
					class={s.fieldWithBadge}
					key="comingSoon"
					label="Coming soon"
					extraContent={live && <Badge class={s.badge} type="live" dark />}
					path={`${this.basePath}/content/releaseMessaging/comingSoon`}
					placeholder={placeholder}
					defaultValue={placeholder}
				>
					{this.renderReleaseDateVariableOptions()}
				</SimpleField>
				{this.renderPreview(`/content/releaseMessaging/comingSoon`, placeholder)}
			</div>
		);
	}

	renderComingSoonWeekOfRelease() {
		const placeholder = this.getMovieCopy(WEEK_OF_RELEASE_COPY_KEY) || 'In Theaters %DAY%';
		const release = this.movie?.dates?.release;
		const releaseNotFullDate = release?.split('-')?.length !== 3;
		const live = (this.movie?.status?.theaters === 'comingSoon' || this.movie?.status?.theaters === 'preSale') && this.movie?.status?.homeEnt !== 'homeEnt' && isReleaseWeek(release);

		return (
			<div class={s.fieldWithPreview}>
				<SimpleField
					class={s.fieldWithBadge}
					key="comingSoonWeekOfRelease"
					label="Coming soon (week of release)"
					path={`${this.basePath}/content/releaseMessaging/comingSoonWeekOfRelease`}
					placeholder={placeholder}
					defaultValue={placeholder}
					help="Monday ahead of release at 00:01 PT"
					extraContent={live && <Badge class={s.badge} type="live" dark />}
					warning={releaseNotFullDate && 'Release day requires a "Full date", please update the Release Date in Movie Info.'}
					disabled={releaseNotFullDate}
				>
					{this.renderReleaseDateVariableOptions()}
				</SimpleField>
				{!releaseNotFullDate && this.renderPreview(`/content/releaseMessaging/comingSoonWeekOfRelease`, placeholder)}
			</div>
		);
	}

	renderInTheaters() {
		const placeholder = this.getMovieCopy(THEATERS_COPY_KEY) || 'Now Playing In Theaters';
		const live = this.movie?.status?.theaters === 'inTheaters' && this.movie?.status?.homeEnt !== 'homeEnt';

		return (
			<SimpleField
				class={s.fieldWithBadge}
				key="inTheaters"
				label="In Theaters"
				help="A movie currently in theaters with an active Showtimes page"
				extraContent={live && <Badge class={s.badge} type="live" dark />}
				path={`${this.basePath}/content/releaseMessaging/inTheaters`}
				placeholder={placeholder}
				defaultValue={placeholder}
			/>
		);
	}

	renderInTheatersAndAtHome() {
		if (!this.homeEntEnabled) return null; // Only show if homeEnt is enabled

		const placeholder = this.getMovieCopy(THEATERS_HOMEENT_COPY_KEY) || 'In Theaters and At Home';
		const live = this.movie?.status?.theaters === 'inTheaters' && this.movie?.status?.homeEnt === 'homeEnt';

		return (
			<SimpleField
				class={s.fieldWithBadge}
				key="inTheatersAndAtHome"
				label="In Theaters & At Home"
				help="A movie in theaters and at home with active Showtimes & HomeEnt pages"
				extraContent={live && <Badge class={s.badge} type="live" dark />}
				path={`${this.basePath}/content/releaseMessaging/inTheatersHomeEnt`}
				placeholder={placeholder}
				defaultValue={placeholder}
			/>
		);
	}

	renderAtHome() {
		if (!this.homeEntEnabled) return null; // Only show if homeEnt is enabled

		const placeholder = this.getMovieCopy(HOMEENT_COPY_KEY) || 'Watch At Home';
		const live = this.movie?.status?.homeEnt === 'homeEnt' && this.movie?.status?.theaters !== 'inTheaters';

		return (
			<SimpleField
				class={s.fieldWithBadge}
				key="atHome"
				label="At Home"
				help="A movie available at home with an active HomeEnt page"
				extraContent={live && <Badge class={s.badge} type="live" dark />}
				path={`${this.basePath}/content/releaseMessaging/homeEnt`}
				placeholder={placeholder}
				defaultValue={placeholder}
			/>
		);
	}

	renderFieldsMoviePlatform() {
		return [
			this.renderTitle(),
			this.renderReleaseDate(),
			this.renderTicketingDate(),
			this.renderHomeEntDate(),
			!this.showReleaseMessagingTab && this.renderReleaseMessaging(),
			this.renderHideFromCarousels(),
			this.renderSlug(),
			this.renderExternalUrl(),
			this.renderShowtimesSource(),
		];
	}

	renderFieldsLocalMoviePage() {
		return [
			this.renderTitle(),
			this.renderReleaseDate(),
			this.renderTicketingDate(),
			!this.showReleaseMessagingTab && this.renderReleaseMessaging(),
			this.renderHideFromCarousels(),
			this.renderSlug(),
			this.renderShowtimesSource(),
		];
	}

	renderFieldsReleaseMessaging() {
		return [
			this.hasReleaseMessagingOverride && this.renderReleaseMessaging({
				label: 'Legacy messaging',
				note: 'Imported from previous version. Remove to use dynamic messaging.',
				help: 'This field will disappear once it has been cleared and saved',
				placeholder: '',
			}),
			this.renderComingSoon(),
			this.renderComingSoonWeekOfRelease(),
			this.renderInTheaters(),
			this.renderInTheatersAndAtHome(),
			this.renderAtHome(),
		];
	}

	renderFields() {
		if (this.movie.placeholder) return this.renderFieldsPlaceholder();
		if (this.state.activeTab === RELEASE_MESSAGING_TAB) return this.renderFieldsReleaseMessaging();
		return this.isLocalMoviePage ? this.renderFieldsLocalMoviePage() : this.renderFieldsMoviePlatform();
	}

	renderDataLayerSectionHeader() {
		return (
			<div class={s.header}>
				<h3>dataLayer Settings</h3>
				<div class={s.description}>
					<span>These fields are used for additional tagging and Essence tracking. They can be populated and edited in Global Settings, requires Global Settings access.</span>
				</div>
			</div>
		);
	}

	renderDataLayerSettings() {
		if (this.state.activeTab !== 'movieInfo') return;
		return super.renderDataLayerSettings();
	}

	renderActionsPlaceholder() {
		return [
			this.renderDeletePlaceholder(),
		];
	}

	renderDescription() {
		const isPlaceholder = this.movie.placeholder;
		if (this.state.activeTab === RELEASE_MESSAGING_TAB) {
			return [
				<p>Change release messaging for this movie.</p>,
				<i class={s.hint}><Info />Add dynamic text by typing "/" to see options.</i>
			];
		}
		return [
			'Controls info related to the movie.',
			isPlaceholder && 'Changes here will be reflected on the upcoming movies page on the hubsite.',
			!isPlaceholder && 'Changes here will be reflected on both the hubsite and movie pages, unless overridden in the page editor.'
		].filter(Boolean).map(str => <p>{str}</p>);
	}

	renderTabs() {
		return [
			<div class={s.tabs}>
				<button 
					class={this.state.activeTab === MOVIE_INFO_TAB && s.active}
					onClick={() => this.setState({ activeTab: MOVIE_INFO_TAB })}
				>
					Movie info
				</button>
				<button 
					class={this.state.activeTab === RELEASE_MESSAGING_TAB && s.active}
					onClick={() => this.setState({ activeTab: RELEASE_MESSAGING_TAB })}
				>
					Release messaging
				</button>
			</div>,
			<div class={s.tabsDivider} />
		];
	}

	renderHeader() {
		const description = this.renderDescription();

		return (
			<div class={s.header}>
				<h2>{this.title || 'Movie info'}</h2>
				{this.showReleaseMessagingTab && this.renderTabs()}
				{description && (<div class={s.description}>{description}</div>)}
			</div>
		);
	}
}